import { useState, KeyboardEvent, ChangeEvent } from 'react';
import { X } from 'lucide-react';

interface EmailChipInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export default function EmailChipInput({ value, onChange, placeholder }: EmailChipInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      setError('Email inválido');
      return;
    }

    if (value.includes(trimmedEmail)) {
      setError('Email ya agregado');
      return;
    }

    onChange([...value, trimmedEmail]);
    setInputValue('');
    setError('');
  };

  const removeEmail = (emailToRemove: string) => {
    onChange(value.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeEmail(value[value.length - 1]);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setError('');

    if (newValue.includes(',') || newValue.includes(' ')) {
      const emails = newValue.split(/[,\s]+/).filter(e => e.trim());
      emails.forEach(email => {
        if (email.trim()) {
          addEmail(email);
        }
      });
      setInputValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  return (
    <div className="email-chip-input-container">
      <div
        className="email-chip-input"
        style={{
          boxShadow: isFocused ? '0 0 0 2px rgba(132, 204, 22, 0.2)' : 'none',
          borderColor: isFocused ? '#84cc16' : undefined,
          transition: 'box-shadow 150ms ease, border-color 150ms ease',
        }}
      >
        {value.map((email) => (
          <span
            key={email}
            className="email-chip"
            style={{
              background: 'rgba(132, 204, 22, 0.1)',
              border: '1px solid rgba(132, 204, 22, 0.2)',
              color: 'var(--text-primary)',
              transition: 'opacity 150ms ease',
            }}
          >
            <span className="email-chip-text">{email}</span>
            <button
              type="button"
              className="email-chip-remove"
              onClick={() => removeEmail(email)}
              aria-label={`Eliminar ${email}`}
              style={{ transition: 'color 150ms ease, transform 150ms ease' }}
            >
              <X size={14} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : ''}
          className="email-chip-input-field"
        />
      </div>
      {error && (
        <div
          className="email-chip-error"
          style={{
            animation: 'chipErrorIn 150ms ease forwards',
          }}
        >
          {error}
        </div>
      )}
      <style>{`
        @keyframes chipErrorIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
